// Aggregated Préparation Fêtes 2027 dashboard.
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export default function PrepDashboardTab({ locale = "fr", onNavigate }) {
  const isFr = locale === "fr";
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/dashboard/prep");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <p style={{ color: "var(--muted)" }}>{isFr ? "Chargement…" : "Loading…"}</p>;
  }

  const dtg = data.days_to_go;
  const dtgColor = dtg <= 30 ? "var(--cinnabar)" : dtg <= 90 ? "var(--gold)" : "var(--moss)";

  return (
    <div className="space-y-6" data-testid="prep-dashboard">
      {/* Hero countdown */}
      <div className="admin-card text-center" style={{ background: "linear-gradient(135deg, var(--parchment-2), white)" }}>
        <div className="font-caps text-[0.7rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
          ◇ {isFr ? "PRÉPARATION DES FÊTES" : "FEAST PREPARATION"} ◇
        </div>
        <h2 className="font-display text-4xl mt-2">2 · 3 · 4 juillet 2027</h2>
        <p className="font-display text-7xl mt-4" style={{ color: dtgColor }} data-testid="prep-days-to-go">
          J–{dtg}
        </p>
        <p className="font-caps text-[0.65rem] mt-2" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>
          {isFr ? "JOURS AVANT L'ÉVÉNEMENT" : "DAYS BEFORE EVENT"}
        </p>
      </div>

      {/* KPI grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Block
          title={isFr ? "Inscriptions" : "Registrations"} testId="prep-block-registrations"
          onClick={() => onNavigate && onNavigate("dashboard")}
          mainValue={data.registrations.paid} mainLabel={isFr ? "payés" : "paid"}
          lines={[
            { l: isFr ? "Encaissé" : "Received", v: `${data.registrations.paid_eur} €` },
            { l: isFr ? "En attente" : "Pending", v: `${data.registrations.pending_eur} €` },
            { l: isFr ? "Pré-inscriptions" : "Pre-reg.", v: data.registrations.pre_registrations },
          ]}
        />
        <Block
          title={isFr ? "Programme" : "Schedule"} testId="prep-block-schedule"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.schedule.validated} mainLabel={`/ ${data.schedule.total} ${isFr ? "validés" : "validated"}`}
          lines={[
            { l: isFr ? "Sous réserves" : "Under review", v: data.schedule.under_review },
          ]}
        />
        <Block
          title={isFr ? "Tâches" : "Tasks"} testId="prep-block-tasks"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.tasks.open} mainLabel={isFr ? "ouvertes" : "open"}
          lines={[
            { l: isFr ? "Terminées" : "Done", v: data.tasks.done, color: "var(--moss)" },
            { l: isFr ? "Bloquées" : "Blocked", v: data.tasks.blocked, color: data.tasks.blocked > 0 ? "var(--cinnabar)" : "var(--muted)" },
          ]}
        />
        <Block
          title={isFr ? "Risques" : "Risks"} testId="prep-block-risks"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.risks.open} mainLabel={isFr ? "ouverts" : "open"}
          mainColor={data.risks.high > 0 ? "var(--cinnabar)" : "var(--gold)"}
          lines={[
            { l: isFr ? "Haute priorité" : "High", v: data.risks.high, color: data.risks.high > 0 ? "var(--cinnabar)" : "var(--muted)" },
          ]}
        />
        <Block
          title={isFr ? "Hébergement" : "Accommodation"} testId="prep-block-housing"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.housing.assigned} mainLabel={`/ ${data.housing.capacity} ${isFr ? "places" : "beds"}`}
          lines={[
            { l: isFr ? "Disponibles" : "Available", v: data.housing.remaining, color: "var(--moss)" },
            { l: isFr ? "Demandes pendantes" : "Pending requests", v: data.housing.pending_requests, color: data.housing.pending_requests > 0 ? "var(--cinnabar)" : "var(--muted)" },
          ]}
        />
        <Block
          title={isFr ? "Restauration" : "Catering"} testId="prep-block-catering"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.catering.expected_meals} mainLabel={isFr ? "repas prévus" : "meals planned"}
          lines={[
            { l: isFr ? "Coût estimé" : "Estimated cost", v: `${data.catering.estimated_cost_eur} €` },
            { l: isFr ? "Lignes prestataires" : "Provider rows", v: data.catering.count },
          ]}
        />
        <Block
          title="Communication" testId="prep-block-communication"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.communication.total} mainLabel={isFr ? "communiqués" : "items"}
          lines={[
            { l: isFr ? "Publiés" : "Published", v: data.communication.published, color: "var(--moss)" },
            { l: isFr ? "Programmés" : "Scheduled", v: data.communication.scheduled },
          ]}
        />
        <Block
          title="Newsletter" testId="prep-block-newsletter"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.newsletter.active} mainLabel={`/ ${data.newsletter.total} ${isFr ? "inscrits" : "subs"}`}
          lines={[]}
        />
        <Block
          title="RH" testId="prep-block-hr"
          onClick={() => onNavigate && onNavigate("projet")}
          mainValue={data.hr.members} mainLabel={isFr ? "membres actifs" : "active members"}
          lines={[
            { l: isFr ? "Bénévoles" : "Volunteers", v: data.hr.benevoles },
            { l: isFr ? "Organisateurs" : "Organisers", v: data.hr.organisateurs },
          ]}
        />
      </div>
    </div>
  );
}

function Block({ title, testId, onClick, mainValue, mainLabel, mainColor, lines = [] }) {
  return (
    <button type="button"
            onClick={onClick}
            data-testid={testId}
            className="admin-card text-left transition-transform hover:scale-[1.02]"
            style={{ cursor: onClick ? "pointer" : "default", width: "100%" }}>
      <div className="font-caps text-[0.6rem] mb-2"
           style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
        {title}
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-4xl" style={{ color: mainColor || "var(--ink)" }}>
          {mainValue}
        </span>
        <span className="text-xs" style={{ color: "var(--muted)" }}>{mainLabel}</span>
      </div>
      {lines.length > 0 && (
        <div className="space-y-1 text-xs" style={{ color: "var(--ink-2)" }}>
          {lines.map((l, i) => (
            <div key={i} className="flex justify-between">
              <span>{l.l}</span>
              <strong style={{ color: l.color || "var(--ink)" }}>{l.v}</strong>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}
